import styled, {css, keyframes} from "styled-components";
import {displaySerifCss, eyebrowTextCss} from "theme/typography";
import {brand, fontFamilies, fontWeights, radii, topbar} from "theme/tokens";
import {advertisingMeasure} from "./AdvertisingContent.styles";

/**
 * Externalized styles for {@link ./AdvertisingHero} (ui#1156, reworked in ui#1861).
 *
 * Every colour is a `theme.tokens` read, so the type, the frame and the drawn stroke all follow light
 * and dark without a second palette.
 *
 * ## What changed, and why the scrim went away
 *
 * The takeover used to run full-bleed BEHIND the words, which needed a two-gradient paper wash over it
 * to keep an `ink2` paragraph legible on an arbitrary sponsor plate. It now runs inside
 * {@link BrowserFrame}, where it belongs: a takeover is a thing that happens on a download page, and
 * showing it on one is both more honest and easier to read than showing it behind a marketing headline.
 * The wash is therefore gone rather than retuned — there is nothing left sitting on the art.
 *
 * The measure is {@link ./AdvertisingContent.styles.advertisingMeasure} — the page's one value, read
 * rather than restated, so the headline and every band below start on one left edge.
 */

/** The hero band. Ordinary flow, no clipping: nothing full-bleed happens here any more. */
export const HeroBand = styled.section`
	${advertisingMeasure};
	padding: calc(${topbar.height} + 32px) 24px 8px;

	@media (max-width: 640px) {
		padding: calc(${topbar.heightMobile} + 24px) 18px 4px;
	}
`;

/**
 * The pitch beside the mock.
 *
 * 5:7 rather than even halves: the frame is a picture and carries its own weight, while the copy column
 * holds a display headline that must not be forced to wrap every three words. Below 960px they stack,
 * copy first — the words are what a reader came for, and the mock explains them rather than the reverse.
 */
export const HeroGrid = styled.div`
	display: grid;
	grid-template-columns: minmax(0, 5fr) minmax(0, 7fr);
	gap: 40px;
	align-items: center;

	@media (max-width: 960px) {
		grid-template-columns: 1fr;
		gap: 28px;
	}
`;

/** Design `.eyebrow` text, without the chip: the page naming its subject above the promise. */
export const HeroEyebrow = styled.p`
	${eyebrowTextCss};
	margin: 0 0 16px;
	color: ${({theme}) => theme.tokens.ink2};
`;

/**
 * The display headline.
 *
 * A wider ramp than {@link theme/typography.displaySerifCss}'s, which is tuned for the home hero's
 * 430px editorial column. It bottoms out at 38px so the wrap at 390px does not push the call to action
 * off the first screen, and its ceiling is lower than the ui#1156 hero's 88px because the headline now
 * shares the row with the frame rather than owning the full width.
 */
export const HeroDisplay = styled.h1`
	${displaySerifCss};
	font-size: clamp(38px, 5.4vw, 72px);
	line-height: 1.03;
	margin: 0;
	/* The stroke hangs below the baseline; without room for it the descender is clipped. */
	padding-bottom: 0.2em;
`;

/**
 * The accented word, and the positioning context for its stroke.
 *
 * `inline-block` so the absolutely positioned stroke can measure itself against the WORD rather than
 * against the whole heading, and `nowrap` so a line break can never split the word out from under a
 * line that was drawn for its full width.
 */
export const AccentWord = styled.span`
	position: relative;
	display: inline-block;
	white-space: nowrap;
`;

/**
 * How long the stroke takes to draw, and how long it waits.
 *
 * The delay is what makes it read as an accent rather than as part of the type: the eye lands on the
 * headline first, and the line is written under it a beat later.
 */
const STROKE_DRAW_SECONDS = "1.2s";
const STROKE_DELAY = "0.45s";

/** Writes the dashed path on, left to right. `pathLength="1"` is what makes the unit `1` mean "all of it". */
const drawStroke = keyframes`
	from {
		stroke-dashoffset: 1;
	}
	to {
		stroke-dashoffset: 0;
	}
`;

/**
 * The drawn stroke's box: slightly wider than the word on both sides, hanging under its baseline.
 *
 * The WIDTH is stated, and that is a correctness fix rather than a preference. An `<svg>` is a REPLACED
 * element, so `width: auto` resolves from its intrinsic aspect ratio — the `viewBox`'s 200x20 — and not
 * from `left` and `right` the way it would for a `<div>`. Anchored by both edges with `width: auto`, the
 * box came out 229px under a 126px word at 1440 and 99px under a 54px word at 390, where it ran past the
 * viewport and was clipped by the band. A percentage of the containing block is the word's own width by
 * construction, at every size.
 *
 * `overflow: visible` because the stroke's round cap and its weight both sit outside the path's own
 * bounding box, and a clipped cap is a line that stops rather than lifts.
 */
export const PencilStroke = styled.svg`
	position: absolute;
	left: -0.05em;
	/*
	 * Low enough to clear a comma-below diacritic. Romanian's accent word is "asteapta" with s-comma and
	 * t-comma, and both descend past the baseline: at -0.06em the stroke ran straight through the comma
	 * under the s. English's "wait" has no descender at all, so the tighter value looked correct in the
	 * only locale that was being looked at.
	 */
	bottom: -0.14em;
	width: calc(100% + 0.1em);
	height: 0.26em;
	overflow: visible;
`;

/**
 * Shared paint for both passes.
 *
 * Brand blue, not the PRO gold. Gold is documented in `theme/tokens` as a SURFACE colour — a near-white
 * lemon measuring 1.04:1 against a white card — so a gold stroke would be invisible on the light theme's
 * paper, which is exactly where this accent has to work hardest. Blue is a foreground token in both
 * modes and is already the headline accent colour the home hero uses.
 *
 * The draw is CSS so it runs from the pre-rendered HTML at first paint, with no hydration involved.
 * Reduced motion does not slow it down or shorten it — it removes the animation and shows the finished
 * line, which is the only correct answer for a mark whose whole content is its final shape.
 */
const strokePaint = css`
	fill: none;
	stroke: ${({theme}) => theme.tokens.blue};
	stroke-linecap: round;
	stroke-dasharray: 1;
	stroke-dashoffset: 1;
	animation: ${drawStroke} ${STROKE_DRAW_SECONDS} cubic-bezier(0.32, 0.72, 0.3, 1) ${STROKE_DELAY} forwards;

	@media (prefers-reduced-motion: reduce) {
		animation: none;
		stroke-dashoffset: 0;
	}
`;

/** The confident first pass. */
export const PencilStrokeMain = styled.path`
	${strokePaint};
	stroke-width: 5;
`;

/**
 * The lighter second pass.
 *
 * Thinner, faded and started later, so the two read as one hand going over the line twice rather than
 * as two lines. Without the offset delay they draw together and the result is a single thick stroke.
 */
export const PencilStrokeTail = styled.path`
	${strokePaint};
	stroke-width: 2.5;
	opacity: 0.5;
	animation-delay: calc(${STROKE_DELAY} + 0.35s);
`;

/** The pitch under the headline. */
export const HeroLede = styled.p`
	margin: 22px 0 0;
	max-width: 30em;
	font-size: clamp(16px, 1.4vw, 18px);
	line-height: 1.6;
	color: ${({theme}) => theme.tokens.ink2};
`;

/**
 * The browser frame: a picture of the surface being sold.
 *
 * `position: relative` and `overflow: hidden` are also the contract `SponsorTakeoverArt` documents for
 * its mount — it is an `inset: 0` absolute layer, so it needs a positioned ancestor to fill and a clip
 * to stop the treatment escaping. The band inside is sized in container units against that layer, which
 * is why the same composition is a wide ribbon here and a scale model in a 210px picker tile.
 */
export const BrowserFrame = styled.div`
	position: relative;
	overflow: hidden;
	border-radius: ${radii.card};
	border: 1px solid ${({theme}) => theme.tokens.cardBorder};
	background: ${({theme}) => theme.tokens.card};
	box-shadow: ${({theme}) => theme.tokens.shadowCard};
`;

/** The chrome across the top. Decorative, and hidden from assistive technology with the rest of the mock. */
export const FrameBar = styled.div`
	display: flex;
	align-items: center;
	gap: 8px;
	height: 36px;
	padding: 0 12px;
	border-bottom: 1px solid ${({theme}) => theme.tokens.line};
	background: ${({theme}) => theme.tokens.card2};
`;

export const FrameDot = styled.span`
	width: 10px;
	height: 10px;
	border-radius: 50%;
	background: ${({theme}) => theme.tokens.line};
`;

export const FrameUrl = styled.span`
	display: flex;
	align-items: center;
	flex: 1;
	min-width: 0;
	height: 20px;
	margin-left: 10px;
	padding: 0 10px;
	border-radius: ${radii.chip};
	background: ${({theme}) => theme.tokens.field};
	font-family: ${fontFamilies.mono};
	font-size: 11px;
	color: ${({theme}) => theme.tokens.ink3};
	overflow: hidden;
	white-space: nowrap;
	text-overflow: ellipsis;
`;

/**
 * The page inside the frame: the positioned, clipped box the live takeover fills.
 *
 * A fixed height rather than an aspect ratio. The card and the ad chip inside are absolutely positioned
 * against it at real sizes, so a box that changed shape with the column width would crush them at one
 * end of the range and strand them at the other.
 *
 * `isolation: isolate` keeps the art's own stacking context from reaching past the frame, so nothing
 * inside a treatment can paint over the page around it.
 */
export const FrameStage = styled.div`
	position: relative;
	overflow: hidden;
	height: 420px;
	background: ${({theme}) => theme.tokens.field};
	isolation: isolate;

	@media (max-width: 640px) {
		height: 460px;
	}
`;

/**
 * The stand-in download card.
 *
 * Presentational only: no button, no anchor, nothing focusable. It is a picture of a card, and a mock
 * that offered a "Download all" a pointer could reach would be offering a transfer that does not exist.
 */
export const MockCard = styled.div`
	position: absolute;
	z-index: 3;
	right: 22px;
	top: 50%;
	transform: translateY(-50%);
	width: min(300px, calc(100% - 44px));
	box-sizing: border-box;
	padding: 18px 18px 14px;
	border-radius: ${radii.card};
	background: ${({theme}) => theme.tokens.card};
	border: 1px solid ${({theme}) => theme.tokens.cardBorder};
	box-shadow: ${({theme}) => theme.tokens.shadowCard};

	/* Stacked at the top so the ad chip below it still has a band of art between them to sit on. */
	@media (max-width: 640px) {
		right: 16px;
		left: 16px;
		top: 18px;
		transform: none;
		width: auto;
	}
`;

export const MockSender = styled.p`
	margin: 0;
	font-size: 12.5px;
	color: ${({theme}) => theme.tokens.ink2};
`;

export const MockCount = styled.p`
	margin: 2px 0 12px;
	font-family: ${fontFamilies.displaySerif};
	font-weight: ${fontWeights.display};
	font-size: 22px;
	line-height: 1.1;
	color: ${({theme}) => theme.tokens.ink};
`;

export const MockFileRow = styled.div`
	display: flex;
	align-items: center;
	gap: 10px;
	padding: 10px 0;
	border-top: 1px solid ${({theme}) => theme.tokens.line};
	font-size: 13.5px;
`;

/**
 * The file-type badge.
 *
 * `brand.chipPurple` and `brand.error` rather than a per-mode token: these are the SAME two colours the
 * real download page paints an MP4 and a PDF chip with, and a file badge that changed hue with the
 * theme would stop reading as the same object it is imitating. White ink on both in either mode.
 */
export const MockFileKind = styled.span<{$kind: "video" | "document"}>`
	display: grid;
	place-items: center;
	flex: none;
	width: 30px;
	height: 30px;
	border-radius: ${radii.chip};
	background: ${({$kind}) => ($kind === "video" ? brand.chipPurple : brand.error)};
	color: #ffffff;
	font-family: ${fontFamilies.mono};
	font-size: 9px;
	font-weight: 700;
`;

export const MockFileName = styled.span`
	flex: 1;
	min-width: 0;
	overflow: hidden;
	white-space: nowrap;
	text-overflow: ellipsis;
	font-weight: 500;
	color: ${({theme}) => theme.tokens.ink};
`;

export const MockFileSize = styled.span`
	flex: none;
	font-variant-numeric: tabular-nums;
	color: ${({theme}) => theme.tokens.ink3};
`;

/** The card's primary action, drawn but not offered — see {@link MockCard}. */
export const MockAction = styled.span`
	display: flex;
	align-items: center;
	justify-content: center;
	height: 42px;
	margin-top: 12px;
	border-radius: ${radii.control};
	background: ${({theme}) => theme.tokens.blue};
	color: ${({theme}) => theme.tokens.blueInk};
	font-size: 14px;
	font-weight: 700;
`;

/** The footer the real unbranded download page carries, shown rather than quietly left out. */
export const MockFoot = styled.p`
	margin: 10px 0 0;
	font-size: 11px;
	text-align: center;
	color: ${({theme}) => theme.tokens.ink3};
`;

/**
 * The stand-in ad, inside the frame.
 *
 * A token-only card, and the one part of the mock where that was a decision rather than a default. The
 * REAL `SponsorTakeoverChip` is a 560px horizontal row — label, copy and button side by side — sized for
 * a recipient's full-width page. Dropped into a 300px slot inside a scale model of that page, its
 * `nowrap` headline ellipsised to "Your headline he..." and its tagline to "An optional second l...":
 * a mock that reads as broken rather than as a narrow page. Getting it to fit would have meant
 * overriding the real chip's geometry from here, which is this page quietly disagreeing with the
 * recipient page about what an ad looks like — worse than a stand-in that admits what it is.
 *
 * So the LAYOUT is a stand-in and the DISCLOSURE is not: {@link AdLabel} is imported from
 * `sponsor/takeover/SponsorTakeover.styles` and fed `sponsorTakeover.label`, so the "Ad" a reader sees
 * here is the same component, the same styling and the same catalog string a recipient sees. That is
 * the part the 2026-08-06 review is about, and it is the part that must not be a marketing rendition.
 *
 * Nothing in the card is focusable: it is a picture, and a "Learn more" a pointer could reach would
 * point nowhere.
 */
export const MockAdCard = styled.div`
	position: absolute;
	z-index: 3;
	left: 20px;
	bottom: 20px;
	width: 260px;
	max-width: calc(100% - 40px);
	box-sizing: border-box;
	padding: 14px 16px 16px;
	border-radius: ${radii.card};
	background: ${({theme}) => theme.tokens.card};
	border: 1px solid ${({theme}) => theme.tokens.cardBorder};
	box-shadow: ${({theme}) => theme.tokens.shadowCard};

	@media (max-width: 640px) {
		left: 16px;
		right: 16px;
		bottom: 16px;
		width: auto;
		max-width: none;
	}
`;

export const MockAdHeadline = styled.p`
	margin: 8px 0 0;
	font-size: 15px;
	font-weight: 700;
	line-height: 1.25;
	color: ${({theme}) => theme.tokens.ink};
`;

/** The sponsor's name, in the real chip's own quieter treatment. */
export const MockAdSponsor = styled.span`
	font-size: 12.5px;
	font-weight: 500;
	color: ${({theme}) => theme.tokens.ink2};
`;

export const MockAdTagline = styled.p`
	margin: 4px 0 0;
	font-size: 13px;
	line-height: 1.4;
	color: ${({theme}) => theme.tokens.ink2};
`;

/** The sponsor's one link, drawn but not offered — see {@link MockAdCard}. */
export const MockAdCta = styled.span`
	display: inline-flex;
	align-items: center;
	height: 32px;
	margin-top: 10px;
	padding: 0 14px;
	border-radius: ${radii.control};
	background: ${({theme}) => theme.tokens.field};
	color: ${({theme}) => theme.tokens.ink};
	font-size: 13px;
	font-weight: 700;
`;

/**
 * The caption naming what is running inside the frame.
 *
 * `ink2` rather than the fainter `ink3`, which `theme/tokens` records as a ~2.5:1 decorative tone in
 * both modes — this sentence is the one that stops a moving backdrop being mistaken for somebody's real
 * campaign, so it has to be readable.
 */
export const DemoCaption = styled.p`
	margin: 12px 0 0;
	font-size: 12.5px;
	line-height: 1.5;
	color: ${({theme}) => theme.tokens.ink2};
`;
